import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { User, UserPayload, UserResponse, UserFilters } from './types';
import { fetchUsers, createUser, updateUser, deleteUser } from './usersApi';
import type { DeleteUserApiPayload } from './types';

interface UsersState {
  items: User[];
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    pages: number;
  };
  sortBy: string;
  sortDir: 'asc' | 'desc';
  search: string;
}

const initialState: UsersState = {
  items: [],
  loading: false,
  error: null,
  pagination: { page: 1, pageSize: 20, total: 0, pages: 0 },
  sortBy: 'email',
  sortDir: 'asc',
  search: ''
};

export const loadUsers = createAsyncThunk<UserResponse, UserFilters | undefined>(
  'users/load',
  async (params) => fetchUsers(params)
);
export const addUser = createAsyncThunk<User, UserPayload>('users/add', async (payload) => createUser(payload));
export const editUser = createAsyncThunk<User, { id: number; payload: UserPayload }>(
  'users/edit',
  async ({ id, payload }) => updateUser(id, payload)
);
export const removeUser = createAsyncThunk<
  number,
  { id: number; payload?: DeleteUserApiPayload },
  { rejectValue: string }
>('users/remove', async ({ id, payload }, { rejectWithValue }) => {
  try {
    await deleteUser(id, payload);
    return id;
  } catch (error: any) {
    const msg =
      typeof error?.message === 'string' ? error.message : 'No se pudo eliminar el usuario';
    return rejectWithValue(msg);
  }
});

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    setUsersSort(state, action: PayloadAction<{ sortBy: string; sortDir: 'asc' | 'desc' }>) {
      state.sortBy = action.payload.sortBy;
      state.sortDir = action.payload.sortDir;
    },
    setUsersPage(state, action: PayloadAction<number>) {
      state.pagination.page = action.payload;
    },
    setUsersPageSize(state, action: PayloadAction<number>) {
      state.pagination.pageSize = action.payload;
    },
    setUsersSearch(state, action: PayloadAction<string>) {
      state.search = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadUsers.fulfilled, (state, action: PayloadAction<UserResponse>) => {
        state.loading = false;
        state.items = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(loadUsers.rejected, (state) => {
        state.loading = false;
        state.error = 'No se pudieron cargar los usuarios';
      })
      .addCase(addUser.fulfilled, (state, action) => {
        state.items = [...state.items, action.payload].sort((a, b) => a.email.localeCompare(b.email));
      })
      .addCase(editUser.fulfilled, (state, action) => {
        state.items = state.items
          .map((u) => (u.id === action.payload.id ? action.payload : u))
          .sort((a, b) => a.email.localeCompare(b.email));
      })
      .addCase(removeUser.fulfilled, (state, action) => {
        state.items = state.items.filter((u) => u.id !== action.payload);
      })
      .addCase(removeUser.rejected, (state, action) => {
        if (action.payload) {
          state.error = action.payload;
        }
      });
  }
});

export const { setUsersSort, setUsersPage, setUsersPageSize, setUsersSearch } = usersSlice.actions;

export default usersSlice.reducer;
